import { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Col, Result, Row } from "antd";
import jsPDF from "jspdf";
import SummaryContent from "../components/company-summary/SummaryContent";
import SummaryHeader from "../components/company-summary/SummaryHeader";
import {
  getCompanyDetails,
  getEmployeeList,
  getDepartmentList,
  getSimilarCompanyList,
  downloadCompany,
} from "../actionCreator/companyDetailsActionCreator";
import "../assets/css/dynemic-page.css";
import Loader from "../components/loader";
import SimilarCompany from "../components/company-summary/similarCompany";
import CompanyPdfFormat from "../components/company-summary/CompanyPdfFormat";
import ViewEmailPhoneModal from "../common/ViewEmailPhoneModal";
import { AuthContext } from "../context/AuthContext";
import { useAlert } from "../context/AlertProvider";
import Meta from "./../pages/Meta";

const CompanySummary = () => {
  const { id } = useParams();
  const { isAuthenticated: isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const companyDetails = useSelector(
    (state) => state.companyDetailsReducer.companyDetails,
  );
  const { isCompanyView } = useContext(AuthContext);

  useEffect(() => {
    if (!isCompanyView && isLoggedIn) {
      navigate("/dashboard-view");
    }
  }, [isCompanyView, navigate, isLoggedIn]);

  useEffect(() => {
    const loadCompanySummary = async () => {
      setLoading(true);
      setIsUnavailable(false);
      dispatch(getDepartmentList());

      const companyResponse = await dispatch(getCompanyDetails(id));

      if (companyResponse?.error && companyResponse?.status === 404) {
        setIsUnavailable(true);
        setLoading(false);
        return;
      }

      await dispatch(getEmployeeList(id, null, page));
      setLoading(false);
    };

    loadCompanySummary();
  }, [id, dispatch, page]);

  useMemo(() => {
    if (Object.keys(companyDetails).length) {
      dispatch(
        getSimilarCompanyList(
          {
            selectedIndustry: companyDetails?.industry
              ? [companyDetails?.industry]
              : [{ id: 0 }],
            cid: companyDetails?.id,
          },
          {
            start: 0,
            end: 10,
          },
        ),
      );
    }
  }, [companyDetails]);

  const downloadPDF = async () => {
    if (isLoggedIn) {
      const res = await dispatch(downloadCompany([companyDetails?.id], "pdf"));
      if (res) {
        let jsPdf = new jsPDF("p", "pt", "letter");
        var htmlElement = document.getElementById("doc-target");

        htmlElement.querySelectorAll("td, th").forEach((cell) => {
          cell.style.whiteSpace = "pre-wrap";
          cell.style.letterSpacing = "0.5px";
          cell.style.whiteSpace = "normal";
          cell.style.wordBreak = "break-word";
        });

        const opt = {
          callback: function (jsPdf) {
            jsPdf.save(`${companyDetails?.name}.pdf`);
            showAlert(
              "success",
              "Download Complete",
              <div>
                The company details for {companyDetails?.name} have been
                successfully downloaded to{" "}
                <FileText className="tw-inline tw-w-4 tw-h-4 tw-text-rose-500" />.
              </div>,
            );
          },
          margin: [30, 30, 30, 30],
          autoPaging: "text",
          html2canvas: {
            useCORS: true,
            allowTaint: true,
            dpi: 300,
            letterRendering: true,
            logging: false,
            scale: 0.8,
          },
        };

        jsPdf.html(htmlElement, opt);
      }
    } else {
      setShowModal(true);
    }
  };

  return (
    <>
      <Meta
        title={
          companyDetails?.name
            ? `${companyDetails?.name} Email ID | Official Contacts`
            : undefined
        }
        description={
          companyDetails?.name
            ? `Get the ${companyDetails?.name} Email ID of Employees including CEO, HR, Marketer etc. All emails are human verified & 100% accurate.`
            : undefined
        }
      />

      {!loading && isUnavailable ? (
        <div className="tw-min-h-[60vh] tw-flex tw-items-center tw-justify-center tw-px-4">
          <Result
            status="404"
            title="This company is no longer available."
            subTitle="The page you requested is not available."
            extra={
              <Button
                type="primary"
                className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
                onClick={() => navigate("/search-company")}
              >
                Back to Company Search
              </Button>
            }
          />
        </div>
      ) : !loading ? (
        <div className={`companysummary ${!isLoggedIn && "padd-90"} p-20 tw-font-inter tw-text-text-primary`}>
          <Row gutter={[20, 20]} className="tw-justify-between">
            <Col xs={24} lg={16} xl={18}>
              <SummaryHeader downloadPDFCallback={downloadPDF} />

              <SummaryContent />
            </Col>
            <Col xs={24} lg={8} xl={6}>
              <SimilarCompany />
            </Col>
          </Row>
        </div>
      ) : (
        <Loader />
      )}

      {showModal && (
        <ViewEmailPhoneModal
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
      <div style={{ position: "absolute", left: "-5000px", top: "-5000px" }}>
        <div id="doc-target">
          <CompanyPdfFormat />
        </div>
      </div>
    </>
  );
};

export default CompanySummary;
